import { Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core';
import { DropdownFilterModel, ParentChildDropdown } from '../../../../core/models/common/dropdown-model';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { DEBOUNCE_TIME_SLOW } from '../../../constants/app.constant';

@Component({
    selector: 'app-dropdown-hierarchical-multiselect',
    templateUrl: './dropdown-hierarchical-multiselect.component.html',
    styleUrls: [
        '../dropdown-form/dropdown-form.component.scss', 
        './dropdown-hierarchical-multiselect.component.scss'
    ],
})
export class DropdownHierarchicalMultiselectComponent implements OnChanges, OnDestroy {
    @ViewChild('dropdownMenu') dropdownMenu!: ElementRef;

    @Input() dropdownListInput?: ParentChildDropdown[];
    dropdownList: ParentChildDropdown[] = [];
    filteredDropdownList: RecursiveDropdownInterface[] = [];

    @Input() selectedItemsInput?: ParentChildDropdown[];
    selectedItems: ParentChildDropdown[] = [];

    @Input() restrictOneParentOnly: boolean = false;

    @Output() selectionChange = new EventEmitter<ParentChildDropdown[]>();
    params = new DropdownFilterModel({});

    isLoading = false;
    showMenu = false;

    maxCharacterLimit = 50;

    // Subject for debounced filter
    private filterSubject$ = new Subject<string>();

    constructor(private translateService: TranslateService) {
        // Set up debounced filter with 300ms delay
        this.filterSubject$
            .pipe(
                debounceTime(DEBOUNCE_TIME_SLOW),
                distinctUntilChanged()
            )
            .subscribe((keyword: string) => {
                this.performFilter(keyword);
            });
    }

    ngOnChanges(changes: SimpleChanges): void {
        // Process dropdown list changes first
        if (
            changes['dropdownListInput'] &&
            changes['dropdownListInput'].currentValue
        ) {
            this.dropdownList = this.flattenCategories(changes['dropdownListInput'].currentValue);
            // Initialize filtered list with the full list (deep copy to avoid state sharing)
            this.filteredDropdownList = this.deepCopyTree(this.dropdownList);
        }

        // Process selected items changes
        if (changes['selectedItemsInput']) {
            const currentValue = changes['selectedItemsInput'].currentValue;

            if (currentValue && currentValue.length > 0) {
                // Selected items provided - flatten the hierarchy to get all selected items
                // This ensures both parent and child categories are in the selectedItems array
                const flattenedCategories = this.flattenCategories(currentValue);

                // Extract all category IDs from the flattened structure recursively
                // Use a Set to track IDs and prevent duplicates
                const allSelectedItems: RecursiveDropdownInterface[] = [];
                const seenIds = new Set<string>();

                const extractAllItems = (categories: RecursiveDropdownInterface[]) => {
                    for (const category of categories) {
                        // Only add if we haven't seen this ID before
                        if (!seenIds.has(category.id)) {
                            seenIds.add(category.id);
                            allSelectedItems.push({
                                id: category.id,
                                name: category.name,
                                level: category.level,
                                isExpanded: category.isExpanded,
                                parentId: category.parentId,
                                childCategories: category.childCategories
                            });
                        }
                        if (category.childCategories && category.childCategories.length > 0) {
                            extractAllItems(category.childCategories);
                        }
                    }
                };
                extractAllItems(flattenedCategories);

                this.selectedItems = allSelectedItems;

                // Developer Note (Be Careful): Triggering this can cause an
                // infinite refresh loop due to the parent component's linked callback
                // also eventually affecting the "selectedItemsInput" input parameter.
                // this.selectionChange.emit(this.selectedItems);
            }
            else if (currentValue && currentValue.length === 0) {
                // Explicitly cleared
                this.selectedItems = [];
            }
        }
    }

    /**
     * Public method called from template with debounce behavior.
     * Emits the keyword to the Subject for debounced filtering.
     * @param keyword - The search string to filter by
     */
    filterCategoryDropdown(keyword: string): void {
        this.filterSubject$.next(keyword || '');
    }

    /**
     * Immediate filter without debounce (for Enter key press).
     * @param keyword - The search string to filter by
     */
    filterCategoryDropdownImmediate(keyword: string): void {
        this.performFilter(keyword || '');
    }

    /**
     * Performs the actual filtering logic on the dropdown list based on the provided keyword.
     * - Searches case-insensitively in both parent and child category names
     * - When a child matches, shows the child and all its ancestors (full path)
     * - When a parent matches, shows the parent and all its children
     * - Empty keyword shows the full unfiltered list
     * @param keyword - The search string to filter by
     */
    private performFilter(keyword: string): void {
        // If no keyword, show the full list (deep copy to avoid state sharing)
        if (!keyword || keyword.trim() === '') {
            this.filteredDropdownList = this.deepCopyTree(this.dropdownList);
            return;
        }

        const normalizedKeyword = keyword.toLowerCase().trim();
        const matchingIds = new Set<string>();

        // Step 1: Find all items that match the keyword
        this.findMatchingItems(this.dropdownList, normalizedKeyword, matchingIds);

        // Step 2: For each matching item, collect all ancestors and descendants
        const idsToInclude = new Set<string>();
        matchingIds.forEach(id => {
            // Add the matching item itself
            idsToInclude.add(id);
            // Add all ancestors (parent chain)
            this.collectAncestors(id, idsToInclude);
            // Add all descendants (children)
            this.collectDescendants(id, idsToInclude);
        });

        // Step 3: Build the filtered tree structure
        this.filteredDropdownList = this.buildFilteredTree(this.dropdownList, idsToInclude);
    }

    /**
     * Recursively finds items whose names match the keyword
     * @param items - List of items to search
     * @param keyword - Normalized search keyword
     * @param matchingIds - Set to store matching item IDs
     */
    private findMatchingItems(
        items: RecursiveDropdownInterface[],
        keyword: string,
        matchingIds: Set<string>
    ): void {
        for (const item of items) {
            if (item.name.toLowerCase().includes(keyword)) {
                matchingIds.add(item.id);
            }
            if (item.childCategories && item.childCategories.length > 0) {
                this.findMatchingItems(item.childCategories, keyword, matchingIds);
            }
        }
    }

    /**
     * Collects all ancestor IDs for a given item
     * @param itemId - The item ID to start from
     * @param ancestorIds - Set to store ancestor IDs
     */
    private collectAncestors(itemId: string, ancestorIds: Set<string>): void {
        const item = this.findItemById(this.dropdownList, itemId);
        if (item && item.parentId) {
            ancestorIds.add(item.parentId);
            this.collectAncestors(item.parentId, ancestorIds);
        }
    }

    /**
     * Collects all descendant IDs for a given item
     * @param itemId - The item ID to start from
     * @param descendantIds - Set to store descendant IDs
     */
    private collectDescendants(itemId: string, descendantIds: Set<string>): void {
        const item = this.findItemById(this.dropdownList, itemId);
        if (item && item.childCategories && item.childCategories.length > 0) {
            for (const child of item.childCategories) {
                descendantIds.add(child.id);
                this.collectDescendants(child.id, descendantIds);
            }
        }
    }

    /**
     * Builds a filtered tree structure containing only items in the idsToInclude set
     * @param items - Original list of items
     * @param idsToInclude - Set of IDs to include in the filtered result
     * @returns Filtered tree structure
     */
    private buildFilteredTree(
        items: RecursiveDropdownInterface[],
        idsToInclude: Set<string>
    ): RecursiveDropdownInterface[] {
        const filteredItems: RecursiveDropdownInterface[] = [];

        for (const item of items) {
            if (idsToInclude.has(item.id)) {
                const filteredChildren = this.buildFilteredTree(item.childCategories, idsToInclude);
                const filteredItem: RecursiveDropdownInterface = {
                    id: item.id,
                    name: item.name,
                    level: item.level,
                    // Automatically expand items when filtering to show matched descendants
                    isExpanded: filteredChildren.length > 0 ? true : item.isExpanded,
                    parentId: item.parentId,
                    childCategories: filteredChildren
                };
                filteredItems.push(filteredItem);
            }
        }

        return filteredItems;
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent): void {
        if (this.dropdownMenu) {
            if (!this.dropdownMenu.nativeElement.contains(event.target)) {
                this.showMenu = false;
            }
        }
    }

    toggleExpansion(item: RecursiveDropdownInterface): void {
        item.isExpanded = !item.isExpanded;
    }

    toggleSelection(itemId: string, name: string, childCategories: RecursiveDropdownInterface[] = []): void {
        const index = this.selectedItems.findIndex((item) => item.id === itemId);
        const isSelected = index > -1;

        if (isSelected) {
            // Deselecting the item
            this.selectedItems.splice(index, 1);

            // Look up the full item from dropdownList to get actual child categories
            const fullItem = this.findItemById(this.dropdownList, itemId);
            const actualChildCategories = fullItem ? fullItem.childCategories : childCategories;

            // Deselect all child items
            this.deselectChildren(actualChildCategories);

            // Check if the parent should be deselected
            this.checkParentDeselection(itemId);
        }
        else {
            // Deselect other parents if only restricted to 1 parent.
            if (this.restrictOneParentOnly) {
                // Only clear selections if the new item belongs to a different parent hierarchy
                const newItemRootParent = this.getRootParentId(itemId);
                const currentRootParent = this.selectedItems.length > 0 ?
                    this.getRootParentId(this.selectedItems[0].id) :
                    null;

                if (currentRootParent && newItemRootParent !== currentRootParent) {
                    this.selectedItems = [];
                }
            }

            // Selecting the item
            this.selectedItems.push({ id: itemId, name: name, childCategories: [] });

            // Select all child items
            this.selectChildren(childCategories);

            // Select parent(s)
            this.selectParent(itemId);
        }

        this.selectionChange.emit(this.selectedItems);
    }

    isSelected(itemId: string): boolean {
        return this.selectedItems.some((item) => item.id === itemId);
    }

    private selectChildren(children: RecursiveDropdownInterface[]): void {
        for (const child of children) {
            if (!this.isSelected(child.id)) {
                this.selectedItems.push({ id: child.id, name: child.name, childCategories: [] });
            }
            // Recursively select children of the child
            this.selectChildren(child.childCategories);
        }
    }

    private deselectChildren(children: RecursiveDropdownInterface[]): void {
        for (const child of children) {
            const index = this.selectedItems.findIndex((item) => item.id === child.id);
            if (index > -1) {
                this.selectedItems.splice(index, 1);
            }
            // Recursively deselect children of the child
            this.deselectChildren(child.childCategories);
        }
    }

    private selectParent(childId: string): void {
        const child = this.findItemById(this.dropdownList, childId);
        if (child) {
            const parentId = child.parentId;
            if (parentId && !this.isSelected(parentId)) {
                const parent = this.findItemById(this.dropdownList, parentId);
                if (parent) {
                    this.selectedItems.push({
                        id: parentId,
                        name: parent.name,
                        childCategories: [],
                    });
                    // Recursively select the parent
                    this.selectParent(parentId);
                }
            }
        }
    }

    private checkParentDeselection(childId: string): void {
        const child = this.findItemById(this.dropdownList, childId);
        if (child) {
            const parentId = child.parentId;
            if (parentId) {
                // Check if all siblings are deselected
                const allSiblingsDeselected = this.areAllSiblingsDeselected(parentId, childId);

                if (allSiblingsDeselected) {
                    const parentIndex = this.selectedItems.findIndex((item) => item.id === parentId);
                    if (parentIndex > -1) {
                        // Deselect the parent
                        this.selectedItems.splice(parentIndex, 1);

                        // Recursively check and deselect the parent's parent
                        this.checkParentDeselection(parentId);
                    }
                }
            }
        }
    }

    private areAllSiblingsDeselected(parentId: string, excludedChildId: string): boolean {
        const parent = this.findItemById(this.dropdownList, parentId);
        if (parent) {
            const result = parent.childCategories.every(
                (sibling) => sibling.id === excludedChildId || !this.isSelected(sibling.id),
            );
            return result;
        }
        return true; // If parent not found, return true
    }

    private flattenCategories(
        categories: ParentChildDropdown[],
        parentId: string | null = null,
        level: number = 0,
    ): RecursiveDropdownInterface[] {
        return categories.flatMap((category) => {
            const currentItem: RecursiveDropdownInterface = {
                id: category.id,
                name: category.name,
                level: level,
                isExpanded: false,
                parentId: parentId,
                childCategories: this.flattenCategories(category.childCategories, category.id, level + 1),
            };
            return [currentItem];
        });
    }

    private findItemById(items: RecursiveDropdownInterface[], id: string): RecursiveDropdownInterface | undefined {
        for (const item of items) {
            if (item.id === id) {
                return item;
            }
            const found = this.findItemById(item.childCategories, id);
            if (found) {
                return found;
            }
        }
        return undefined;
    }

    private getRootParentId(itemId: string): string {
        const item = this.findItemById(this.dropdownList, itemId);
        if (!item) {
            return itemId;
        }

        // If item has no parent, it's the root
        if (!item.parentId) {
            return itemId;
        }

        // Recursively find the root parent
        return this.getRootParentId(item.parentId);
    }

    /**
     * Creates a deep copy of the tree structure to avoid state sharing
     * @param items - Tree to copy
     * @returns Deep copy of the tree
     */
    private deepCopyTree(items: RecursiveDropdownInterface[]): RecursiveDropdownInterface[] {
        return items.map(item => ({
            id: item.id,
            name: item.name,
            level: item.level,
            isExpanded: item.isExpanded,
            parentId: item.parentId,
            childCategories: this.deepCopyTree(item.childCategories)
        }));
    }

    /**
     * Gets the display text for selected items following the format:
     * - On large devices (>1024px): Shows categories until reaching character limit, then truncates
     * - On mobile/tablet (≤1024px): Shows all selected items, CSS handles truncation with ellipsis
     *
     * Single Responsibility: This method has one reason to change - if display format requirements change
     * SSR Compatible: Pure string manipulation, no browser-specific APIs
     *
     * @returns Formatted display string for selected categories
     */
    getSelectedItemsDisplayText(): string {
        if (this.selectedItems.length === 0) {
            return '';
        }

        // Return all items as comma-separated string
        // CSS will handle truncation on mobile with text-overflow: ellipsis
        // On desktop (>1024px), we truncate programmatically and show remaining count
        return this.selectedItems.map(item => item.name).join(', ');
    }

    /**
     * Gets the display text for large screens only (>1024px)
     * Truncates after character limit for consistent display
     *
     * @returns Truncated text for large screens
     */
    getSelectedItemsDisplayTextLarge(): string {
        if (this.selectedItems.length === 0) {
            return '';
        }

        const displayedItems: string[] = [];
        let currentLength = 0;
        let itemsDisplayed = 0;

        // Add items one by one until we exceed the character limit
        for (let i = 0; i < this.selectedItems.length; i++) {
            const item = this.selectedItems[i];
            const separator = i > 0 ? ', ' : '';
            const itemText = `${separator}${item.name}`;
            const potentialLength = currentLength + itemText.length;

            // If adding this item would exceed the limit and we already have at least one item, stop
            if (potentialLength > this.maxCharacterLimit && itemsDisplayed > 0) {
                break;
            }

            displayedItems.push(item.name);
            currentLength = potentialLength;
            itemsDisplayed++;
        }

        const formattedItems = displayedItems.join(', ');
        return formattedItems;
    }

    /**
     * Gets the remaining count text with proper formatting
     * Only displayed on large devices (>1024px)
     * SSR Compatible: Pure string manipulation, works on both server and client
     *
     * @returns Formatted remaining count text or empty string if no remaining items
     */
    getRemainingCountText(): string {
        if (this.selectedItems.length === 0) {
            return '';
        }

        let currentLength = 0;
        let itemsDisplayed = 0;

        // Count how many items are displayed before truncation
        for (let i = 0; i < this.selectedItems.length; i++) {
            const item = this.selectedItems[i];
            const separator = i > 0 ? ', ' : '';
            const itemText = `${separator}${item.name}`;
            const potentialLength = currentLength + itemText.length;

            if (potentialLength > this.maxCharacterLimit && itemsDisplayed > 0) {
                break;
            }

            currentLength = potentialLength;
            itemsDisplayed++;
        }

        const remainingCount = this.selectedItems.length - itemsDisplayed;

        if (remainingCount > 0) {
            return `... +${remainingCount} ${this.translateService.instant('faq.text.more')}`;
        }

        return '';
    }

    /**
     * Gets the names of remaining items that are not displayed in the truncated text
     * Used for tooltip display on hover
     * SSR Compatible: Pure array manipulation, works on both server and client
     *
     * @returns Comma-separated string of remaining item names
     */
    getRemainingItemsTooltip(): string {
        if (this.selectedItems.length === 0) {
            return '';
        }

        let currentLength = 0;
        let itemsDisplayed = 0;

        // Count how many items are displayed before truncation
        for (let i = 0; i < this.selectedItems.length; i++) {
            const item = this.selectedItems[i];
            const separator = i > 0 ? ', ' : '';
            const itemText = `${separator}${item.name}`;
            const potentialLength = currentLength + itemText.length;

            if (potentialLength > this.maxCharacterLimit && itemsDisplayed > 0) {
                break;
            }

            currentLength = potentialLength;
            itemsDisplayed++;
        }

        // Get the remaining items
        const remainingItems = this.selectedItems.slice(itemsDisplayed);
        return remainingItems.map(item => item.name).join(', ');
    }

    /**
     * Checks if there are any selected items
     * SSR Compatible: Simple boolean check, works on both server and client
     *
     * @returns True if items are selected, false otherwise
     */
    hasSelectedItems(): boolean {
        return this.selectedItems.length > 0;
    }

    /**
     * Checks if the FAQ Category checkbox should show:
     * A [Tick] icon checkbox - [If True]; OR
     * A [Dash] icon checkbox - [If False]; OR
     * [No icon] checkbox - [If False];
     * @returns True if the FAQ Category has children items and only SOME of them were selected.
     */
    isParentIndeterminate(item: RecursiveDropdownInterface): boolean {
        if (item.childCategories.length == 0)
            return false;

        let isAnyChildrenSelected = false;
        let isAllChildrenSelected = true;

        item.childCategories.forEach(child => {
            if (isAnyChildrenSelected && !isAllChildrenSelected)
                return;

            const doesChildExist = this.selectedItems.some(selectedItem => selectedItem.id == child.id);
            
            if (!isAnyChildrenSelected && doesChildExist)
            {
                isAnyChildrenSelected = true;
            }

            if (isAllChildrenSelected && !doesChildExist) {
                isAllChildrenSelected = false;
            }
        })
        return isAnyChildrenSelected && !isAllChildrenSelected;
    }

    ngOnDestroy(): void {
        // Complete the Subject to clean up the subscription
        this.filterSubject$.complete();
    }
}

export interface RecursiveDropdownInterface {
    id: string;
    name: string;
    level?: number;
    isExpanded?: boolean;
    parentId?: string | null;
    childCategories: RecursiveDropdownInterface[];
}
